import React from 'react';
import TapArea from './TapArea';
import SettingsPanel from './SettingsPanel';
import StatsScreen from './StatsScreen';
import RoutineComplete from './RoutineComplete';
import ProUpsell from './ProUpsell';
import AchievementToast from './AchievementToast';
import Demo, { DEMO_STEPS } from './Demo';
import { getRoutinePrompt } from '../routines';
import { FREE_COLOR_KEYS } from '../constants';

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// The actual orb-tapping screen: settings overlay, stats, session/routine
// controls, the onboarding demo overlay, and the two orbs themselves. Split
// out of App.js so every page (Home, Timer, this one, …) is consistently its
// own component rather than one page's JSX living inline in the router.
const MainOrbScreen = ({
  settings, setSettings, saveSettings, hasProAccess, hasBillingAccount, isKidsPreview,
  settingsOpen, setSettingsOpen, openSettingsSignal, closeSettingsSignal,
  advanceDemo, handleSignOut, deleteAccount, manageSubscription, setProPrompt,
  startTimer, openStats, statsView, setStatsView, resetSessionStats, resetLifetimeStats,
  demoStep, demoStepRef, showRotateHint, secondsLeft, resumeFromSession,
  activeRoutine, awaitingResume, routineMode, routinePaused, setRoutinePaused,
  routineTiming, routineProgress, setActiveRoutine, setSelectedRoutine, setCurrentPage,
  resetTimer, resetOrbs, recordCheckIn,
  continueOnboardingWithFreeTap, showOnboardingProOverview, finishDemo,
  effectiveSettings, orb1Animal, orb2Animal, orb1Visible, orb2Visible, orb1Color, orb2Color,
  handleOrbTap, proPrompt, closeProPrompt, startProTrial, startProCheckout, resendVerificationEmail,
  newAchievement, dismissNewAchievement, swipe,
}) => {
  // Keep a route back to the routine library on every real orb session. The
  // first-launch tour remains distraction-free, and modal panels temporarily
  // hide the control so it cannot be activated through an overlay.
  const sessionHomeVisible = !settingsOpen
    && !statsView
    && (activeRoutine ? !awaitingResume : (demoStep < 0 && !showRotateHint));

  const returnToRoutineLibrary = () => {
    resetTimer();
    resetOrbs();
    setActiveRoutine(null);
    setSelectedRoutine(null);
    setRoutinePaused(false);
    setCurrentPage('routines');
  };

  return (
    <div
      className="h-screen w-full relative flex flex-col overflow-hidden bg-black"
      onTouchStart={swipe.onTouchStart}
      onTouchMove={swipe.onTouchMove}
      onTouchEnd={swipe.onTouchEnd}
    >
      {/* Settings Gear Overlay */}
      {!activeRoutine && <SettingsPanel
        settings={settings}
        updateSettings={(patch) => {
          // The onboarding demo lets a free user flip the real Kids Mode
          // switch to preview it (isKidsPreview, passed down from App.js) —
          // everywhere else it's still gated behind Pro.
          if (!hasProAccess && patch.kidsMode && !isKidsPreview) { setProPrompt('kids'); return; }
          if (!hasProAccess && patch.orbColor && !FREE_COLOR_KEYS.includes(patch.orbColor)) {
            setProPrompt('colours');
            return;
          }
          if (demoStep >= 0) setSettings((current) => ({ ...current, ...patch }));
          else saveSettings(patch);
        }}
        onSetTimer={activeRoutine ? undefined : startTimer}
        onOpenChange={setSettingsOpen}
        onOpenStats={openStats}
        openSignal={openSettingsSignal}
        closeSignal={closeSettingsSignal}
        kidsDemo={isKidsPreview}
        demoHighlight={DEMO_STEPS[demoStep]?.key === 'settings'}
        onDemoControlsDone={advanceDemo}
        onSignOut={handleSignOut}
        onDeleteAccount={deleteAccount}
        onManageSubscription={manageSubscription}
        isPro={hasProAccess}
        hasBillingAccount={hasBillingAccount}
        onProRequired={(feature) => setProPrompt(feature || 'kids')}
      />}

      {statsView && (
        <StatsScreen
          stats={statsView}
          hideProgress={settings.hideProgress ?? false}
          onSetProgressVisible={(visible) => saveSettings({ hideProgress: !visible })}
          onResetSession={resetSessionStats}
          onResetLifetime={resetLifetimeStats}
          onClose={() => {
            setStatsView(null);
            // Closing Stats during the tour reveals the final, informational
            // step. No locked feature is demonstrated during onboarding.
            if (DEMO_STEPS[demoStepRef.current]?.key === 'stats') advanceDemo();
          }}
        />
      )}

      {/* First-launch: suggest rotating to landscape while the spheres settle. */}
      {showRotateHint && (
        <div className="absolute inset-x-0 z-chrome flex justify-center pointer-events-none" style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 2.5rem)' }}>
          <div className="flex items-center gap-4 px-7 py-5 rounded-3xl bg-black/60 backdrop-blur-xl border border-indigo-300/30 shadow-2xl shadow-indigo-500/20 text-white">
            <span className="rotate-hint text-5xl">📱</span>
            <span className="text-xl font-bold">Rotate to landscape</span>
          </div>
        </div>
      )}

      {/* Session countdown (top-left). Tap to cancel. */}
      {sessionHomeVisible && (
        <button
          type="button"
          onClick={returnToRoutineLibrary}
          aria-label={activeRoutine ? 'Leave routine and return home' : 'Leave free session and return home'}
          className="absolute z-controls flex h-11 items-center gap-2 rounded-full border border-white/15 bg-black/50 px-4 text-sm font-bold text-white/80 shadow-2xl backdrop-blur-xl active:scale-95"
          style={{
            top: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
            left: 'calc(env(safe-area-inset-left, 0px) + 1rem)',
          }}
        >
          <span aria-hidden="true">⌂</span>
          <span>Home</span>
        </button>
      )}

      {secondsLeft != null && !activeRoutine?.hideCountdown && (
        <button
          type="button"
          onClick={() => {
            if (activeRoutine) {
              if (window.confirm('End this routine and return to the routine list?')) {
                returnToRoutineLibrary();
              }
            } else startTimer(0);
          }}
          aria-label={activeRoutine ? 'End routine' : 'Cancel timer'}
          className="absolute z-controls flex items-center h-11 px-4 rounded-full bg-black/40 backdrop-blur-xl border border-white/10 text-white/80 text-sm font-bold shadow-2xl active:scale-95 transition-transform"
          style={{
            top: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
            left: sessionHomeVisible
              ? 'calc(env(safe-area-inset-left, 0px) + 6.8rem)'
              : 'calc(env(safe-area-inset-left, 0px) + 1rem)',
          }}
        >
          {fmtTime(secondsLeft)}
        </button>
      )}

      {/* End-of-session prompt. Tapping is paused until this is tapped. */}
      {awaitingResume && !activeRoutine && (
        <button
          type="button"
          onClick={resumeFromSession}
          className="absolute inset-0 z-chrome flex items-center justify-center bg-black/30"
        >
          <div className="px-6 py-4 rounded-2xl bg-black/60 backdrop-blur-xl border border-white/10 text-center">
            <p className="text-white font-semibold text-lg">Session complete</p>
            <p className="text-gray-300 text-sm mt-1">Tap anywhere to keep going</p>
          </div>
        </button>
      )}

      {awaitingResume && activeRoutine && (
        <RoutineComplete
          routine={activeRoutine}
          onCheckIn={recordCheckIn}
          onDone={returnToRoutineLibrary}
        />
      )}

      {/* Reserve space above TapArea so routine copy never overlays the orbs. */}
      {activeRoutine && !awaitingResume && (
        <div className="relative z-chrome flex shrink-0 justify-center px-4 pb-4 pointer-events-none" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4.75rem)' }}>
          <div className="pointer-events-auto flex max-w-full items-center gap-3 rounded-3xl border border-white/10 bg-black/50 px-4 py-2 backdrop-blur-xl">
            <span className="min-w-0 max-w-[70vw] text-xs font-semibold text-white/75">
              <span className="block truncate">{activeRoutine.title} · {routineMode === 'watch' ? 'Watch' : 'Tap'}</span>
              <span className="mt-0.5 block text-[0.625rem] font-normal leading-snug text-white/65" aria-live="polite">{getRoutinePrompt(activeRoutine, secondsLeft)}</span>
            </span>
            <button type="button" onClick={() => setRoutinePaused((paused) => !paused)} aria-pressed={routinePaused} className="min-h-11 min-w-11 shrink-0 rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-indigo-200">
              {routinePaused ? 'Resume' : 'Pause'}
            </button>
          </div>
        </div>
      )}

      {/* First-launch interactive demo (overlay; orbs stay tappable beneath).
          Hidden while the settings panel is open on the 'settings'/'kids' steps
          so the flashing cog/controls in the panel aren't covered. */}
      {demoStep >= 0 && !(['settings', 'kids'].includes(DEMO_STEPS[demoStep]?.key) && settingsOpen) && (
        <Demo
          step={demoStep}
          onAdvance={advanceDemo}
          onSkip={() => finishDemo()}
          onContinueFree={continueOnboardingWithFreeTap}
          onSeePro={showOnboardingProOverview}
          kidsActive={settings.kidsMode}
        />
      )}

      {/* Persistent stats shortcut: a gently pulsing "S" in the bottom-right
          corner opens your stats (the corner swipe ↘ still works too). During
          the tour it only appears on the final 'stats' step so it doesn't
          distract from the earlier ones. */}
      {!activeRoutine && (demoStep < 0 || DEMO_STEPS[demoStep]?.key === 'stats') && !statsView && !awaitingResume && !settingsOpen && (
        <button
          type="button"
          onClick={openStats}
          aria-label="Open your stats"
          className="stats-pulse absolute z-controls w-11 h-11 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-xl border border-white/15 shadow-2xl"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
            right: 'calc(env(safe-area-inset-right, 0px) + 1rem)',
          }}
        >
          <span className="text-lg font-extrabold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">S</span>
        </button>
      )}

      {/* The Interactive Game Area: two independent orbs/animals */}
      <TapArea
        kidsMode={effectiveSettings.kidsMode}
        orb1Animal={orb1Animal}
        orb2Animal={orb2Animal}
        orb1Visible={orb1Visible}
        orb2Visible={orb2Visible}
        orb1Color={orb1Color}
        orb2Color={orb2Color}
        orbSize={effectiveSettings.orbSize}
        orbSpacing={effectiveSettings.orbSpacing}
        onOrbTap={handleOrbTap}
        disabled={settingsOpen || routinePaused || awaitingResume || (activeRoutine && routineMode === 'watch')}
        demoActive={demoStep >= 0}
        fadeDurationMs={activeRoutine ? routineTiming.fadeDurationMs : 0}
        routineProgress={routineProgress}
      />

      {proPrompt && (
        <ProUpsell
          feature={proPrompt}
          onClose={closeProPrompt}
          onContinueFree={proPrompt === 'overview' ? continueOnboardingWithFreeTap : undefined}
          onStartTrial={startProTrial}
          onUpgrade={startProCheckout}
          onResendVerification={resendVerificationEmail}
          hasProAccess={hasProAccess}
        />
      )}

      <AchievementToast achievement={settings.hideProgress ? null : newAchievement} onDone={dismissNewAchievement} />
    </div>
  );
};

export default MainOrbScreen;
